import React, { useEffect, useRef, useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import LogFormShell from "../../UI/LogFormShell";
import colors from "../../../assets/theme/colors";
import { updateCaregiverAuthoredProfileFact } from "../../../features/knowledgeItems";

const formatLifecycleDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
};

/**
 * Shared caregiver-owned-fact edit sheet, reused by every Handbook chapter
 * that has adopted source-routed editing. Domain-agnostic: it only knows
 * about a fact's `summary`/`capturedAt`/`updatedAt` and the entry's headline
 * (used purely as a subtitle) - it has no idea which chapter/domain launched
 * it. Loads and writes only the underlying Knowledge fact's text, never a
 * generated Handbook headline/explanation.
 */
const HandbookFactEditSheet = ({ open, entry, fact, readOnly, fullEntry = false, onClose, onSaved }) => {
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [conflict, setConflict] = useState(null); // { latestSummary } | null
  const [conflictAcknowledged, setConflictAcknowledged] = useState(false);
  const baselineSummaryRef = useRef("");

  useEffect(() => {
    if (!open) return;
    // Always the original caregiver-entered source text - never the generated
    // Handbook headline/explanation.
    baselineSummaryRef.current = fact?.summary || "";
    setDraft(fact?.summary || "");
    setSaveError("");
    setConflict(null);
    setConflictAcknowledged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !fact || isSaving) return;
    const latest = fact.summary || "";
    const draftDivergedFromBaseline = draft.trim() !== baselineSummaryRef.current.trim();
    const sourceChangedUnderneath = latest.trim() !== baselineSummaryRef.current.trim();
    if (sourceChangedUnderneath && draftDivergedFromBaseline && !conflictAcknowledged) {
      setConflict({ latestSummary: latest });
    }
  }, [fact, open, draft, isSaving, conflictAcknowledged]);

  if (!open) return null;

  const canLoadSource = Boolean(fact);
  const hasChanges = canLoadSource && draft.trim() !== (fact.summary || "").trim();
  const hasUnresolvedConflict = Boolean(conflict) && !conflictAcknowledged;

  const handleReloadLatest = () => {
    const latest = conflict?.latestSummary ?? fact?.summary ?? "";
    setDraft(latest);
    baselineSummaryRef.current = latest;
    setConflict(null);
    setConflictAcknowledged(false);
  };

  const handleKeepEditing = () => {
    setConflictAcknowledged(true);
  };

  const handleSave = async () => {
    if (isSaving || !canLoadSource || readOnly) return;
    // Client-side, best-effort guard only: the Knowledge service has no
    // version/transaction check, so this cannot prevent a true race, only an
    // accidental silent overwrite when the caregiver has this sheet open long
    // enough for someone else's edit to arrive first.
    if (hasUnresolvedConflict) return;

    const trimmed = draft.trim();
    if (!trimmed) {
      setSaveError("This can't be saved empty.");
      return;
    }

    setIsSaving(true);
    setSaveError("");
    try {
      await updateCaregiverAuthoredProfileFact(fact, trimmed);
      onSaved(fact.id, trimmed);
    } catch (error) {
      setSaveError(error?.message || "We couldn't save this right now. Your changes are still here.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    onClose();
  };

  const addedDateLabel = fact?.capturedAt ? formatLifecycleDate(fact.capturedAt) : "";
  const lifecycleLine = [
    addedDateLabel ? `${fullEntry ? "Added by you ·" : "Added"} ${addedDateLabel}` : "",
    fact?.updatedAt && fact.updatedAt !== fact.capturedAt ? `Updated ${formatLifecycleDate(fact.updatedAt)}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <LogFormShell
      open={open}
      onClose={handleCancel}
      title={fullEntry ? "Your full entry" : readOnly ? "View details" : "Edit"}
      subtitle={entry?.headline}
      maxWidth="sm"
      forceDrawer
      footer={!canLoadSource ? (
        <Button onClick={onClose} fullWidth sx={{ textTransform: "none", fontWeight: 800 }}>
          Close
        </Button>
      ) : readOnly ? (
        <Button onClick={onClose} fullWidth variant="contained" sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999 }}>
          Close
        </Button>
      ) : hasUnresolvedConflict ? null : (
        <Stack direction="row" spacing={1}>
          <Button onClick={handleCancel} disabled={isSaving} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            sx={{ flex: 1, textTransform: "none", fontWeight: 800, borderRadius: 999 }}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      )}
    >
      {!canLoadSource ? (
        <Typography sx={{ fontSize: "0.94rem", color: colors.semantic.ink2 }}>
          We can't load this right now. Try again in a moment.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {hasUnresolvedConflict ? (
            <Stack spacing={0.75} sx={{ p: 1.1, borderRadius: "12px", bgcolor: "#FFF4E5" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: "#8A5A00" }}>
                This information was updated elsewhere.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleReloadLatest} sx={{ textTransform: "none", fontWeight: 800 }}>
                  Reload latest
                </Button>
                <Button size="small" variant="outlined" onClick={handleKeepEditing} sx={{ textTransform: "none", fontWeight: 800 }}>
                  Keep editing
                </Button>
              </Stack>
            </Stack>
          ) : null}
          {fullEntry ? (
            <Typography sx={{ fontSize: "0.98rem", lineHeight: 1.65, color: colors.semantic.ink }}>
              {draft}
            </Typography>
          ) : (
            <TextField
              label="What caregivers should know"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              autoFocus
              disabled={readOnly}
            />
          )}
          {lifecycleLine ? (
            <Typography sx={{ fontSize: "0.78rem", color: colors.semantic.ink2 }}>
              {lifecycleLine}
            </Typography>
          ) : null}
          {saveError ? (
            <Typography role="alert" sx={{ color: "#B3261E", fontSize: "0.86rem", fontWeight: 700 }}>
              {saveError}
            </Typography>
          ) : null}
        </Stack>
      )}
    </LogFormShell>
  );
};

export default HandbookFactEditSheet;
